import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  PatientDTOSchema,
  CreatePatientSchema,
  UpdatePatientSchema,
} from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";

const ShareEntry = z.object({
  doctorId: z.string(),
  grantedById: z.string(),
  createdAt: z.string(),
  doctorName: z.string(),
  doctorEmail: z.string(),
});

export const PatientDetailSchema = PatientDTOSchema.extend({
  shares: z.array(ShareEntry),
});

export const PatientSearchItemSchema = PatientDTOSchema.extend({
  isOwn: z.boolean().optional(),
});
export type PatientSearchItem = z.infer<typeof PatientSearchItemSchema>;

const PatientSearchResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(PatientSearchItemSchema),
});

const PatientCreateResponseSchema = z.object({
  success: z.boolean(),
  data: PatientDTOSchema,
});

export function usePatient(id: string | null | undefined) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["patient", id],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/patients/${id}`,
      });
      return PatientDetailSchema.parse(
        (json as { success: boolean; data: unknown }).data,
      );
    },
    enabled: !!id,
  });
}

export function usePatientSearch(query: string) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["patients-search", query],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/patients/search?q=${encodeURIComponent(query)}`,
      });
      return PatientSearchResponseSchema.parse(json).data;
    },
    enabled: query.trim().length >= 1,
  });
}

export function useCreatePatient() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: z.infer<typeof CreatePatientSchema>) =>
      backend({ method: "POST", url: "/api/v1/patients", data }),
    onSuccess: (json) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      return PatientCreateResponseSchema.parse(json).data;
    },
  });
}

export function useUpdatePatient() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: z.infer<typeof UpdatePatientSchema>;
    }) =>
      backend({ method: "PATCH", url: `/api/v1/patients/${id}`, data }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["patient", id] });
    },
  });
}

export function useDeletePatient() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/patients/${id}` }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["patients"] }),
  });
}

export function useSharePatient() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      doctorId,
    }: {
      patientId: string;
      doctorId: string;
    }) =>
      backend({
        method: "POST",
        url: `/api/v1/patients/${patientId}/share`,
        data: { doctorId },
      }),
    onSuccess: (_, { patientId }) =>
      queryClient.invalidateQueries({ queryKey: ["patient", patientId] }),
  });
}

export function useRevokePatientShare() {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      patientId,
      doctorId,
    }: {
      patientId: string;
      doctorId: string;
    }) =>
      backend({
        method: "DELETE",
        url: `/api/v1/patients/${patientId}/share/${doctorId}`,
      }),
    onSuccess: (_, { patientId }) =>
      queryClient.invalidateQueries({ queryKey: ["patient", patientId] }),
  });
}
